import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';
import { ApiError, ClientOptions } from '../types';

export class BaseClient {
  protected axiosInstance: AxiosInstance;
  protected apiToken: string;
  protected baseUrl: string;

  constructor(options: ClientOptions) {
    this.apiToken = options.apiToken;
    this.baseUrl = options.baseUrl || 'https://api.chatsuite.com';

    this.axiosInstance = axios.create({
      baseURL: this.baseUrl,
      timeout: options.timeout || 30000,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.apiToken}`,
        ...options.headers,
      },
    });

    // Add request interceptor for auth
    this.axiosInstance.interceptors.request.use(
      (config) => {
        // Ensure auth header is always present
        if (!config.headers.Authorization) {
          config.headers.Authorization = `Bearer ${this.apiToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Add response interceptor for error handling
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        return Promise.reject(this.handleError(error));
      }
    );

    // Add retry logic if configured
    if (options.retryConfig) {
      this.setupRetryInterceptor(options.retryConfig);
    }
  }

  private setupRetryInterceptor(retryConfig: ClientOptions['retryConfig']) {
    this.axiosInstance.interceptors.response.use(
      undefined,
      async (error: AxiosError) => {
        const config = error.config as AxiosRequestConfig & { _retry?: number };
        
        if (!config || !retryConfig) {
          return Promise.reject(error);
        }

        config._retry = config._retry || 0;

        if (
          config._retry >= (retryConfig.retries || 3) ||
          (retryConfig.retryCondition && !retryConfig.retryCondition(error))
        ) {
          return Promise.reject(error);
        }

        config._retry += 1;

        // Wait before retrying
        const retryCount = config._retry || 1;
        await new Promise(resolve => 
          setTimeout(resolve, retryConfig.retryDelay || 1000 * retryCount)
        );

        return this.axiosInstance(config);
      }
    );
  }

  private handleError(error: AxiosError): ApiError {
    const apiError = new Error() as ApiError;

    if (error.response) {
      // Server responded with error
      const responseData = error.response.data as any;
      apiError.message = responseData?.error || responseData?.message || error.message;
      apiError.status = error.response.status;
      apiError.code = responseData?.code;
      apiError.details = responseData;
    } else if (error.request) {
      // Request made but no response
      apiError.message = 'No response from server';
      apiError.code = 'NETWORK_ERROR';
    } else {
      // Request setup error
      apiError.message = error.message;
      apiError.code = 'REQUEST_ERROR';
    }

    apiError.name = 'WhatsAppApiError';
    return apiError;
  }

  protected async get<T>(path: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.get<T>(path, config);
    return response.data;
  }

  protected async post<T>(path: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.post<T>(path, data, config);
    return response.data;
  }

  protected async put<T>(path: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.put<T>(path, data, config);
    return response.data;
  }

  protected async delete<T>(path: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.delete<T>(path, config);
    return response.data;
  }

  protected async patch<T>(path: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.patch<T>(path, data, config);
    return response.data;
  }

  /**
   * Update the API token
   */
  public setApiToken(token: string): void {
    this.apiToken = token;
    this.axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  /**
   * Get the current base URL
   */
  public getBaseUrl(): string {
    return this.baseUrl;
  }
}